import { useEffect } from "react";

export const SITE_URL = String(
  process.env.REACT_APP_SITE_URL || "https://grapheweddings.com"
).replace(/\/$/, "");

const DEFAULT_IMAGE = `${SITE_URL}/preview.jpg`;

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

export const absoluteUrl = (value, fallback = DEFAULT_IMAGE) => {
  if (!value) return fallback;

  try {
    return new URL(value, `${SITE_URL}/`).href;
  } catch {
    return fallback;
  }
};

export const toPlainText = (value, maxLength = 160) => {
  const text = String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
};

const Seo = ({
  title,
  description,
  path = "/",
  canonicalUrl,
  robots = "index, follow, max-image-preview:large",
  image = DEFAULT_IMAGE,
  type = "website",
  structuredData,
  structuredDataId = "page",
}) => {
  useEffect(() => {
    const canonical = canonicalUrl || absoluteUrl(path, `${SITE_URL}/`);
    const shareImage = absoluteUrl(image);
    const finalDescription = toPlainText(description);

    document.title = title;
    upsertCanonical(canonical);

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: finalDescription,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: robots,
    });
    upsertMeta('meta[name="googlebot"]', {
      name: "googlebot",
      content: robots,
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: finalDescription,
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonical,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: shareImage,
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: finalDescription,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: shareImage,
    });

    const selector = `script[data-seo-jsonld="${structuredDataId}"]`;
    let jsonLd = document.head.querySelector(selector);

    if (structuredData) {
      if (!jsonLd) {
        jsonLd = document.createElement("script");
        jsonLd.type = "application/ld+json";
        jsonLd.dataset.seoJsonld = structuredDataId;
        document.head.appendChild(jsonLd);
      }

      jsonLd.textContent = JSON.stringify(structuredData).replace(
        /</g,
        "\\u003c"
      );
    } else {
      jsonLd?.remove();
    }

    return () => {
      document.head.querySelector(selector)?.remove();
    };
  }, [
    canonicalUrl,
    description,
    image,
    path,
    robots,
    structuredData,
    structuredDataId,
    title,
    type,
  ]);

  return null;
};

export default Seo;
